// src/components/Navbar.jsx
import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-jalaali";
import NotificationsDropdown from "./NotificationsDropdown";
import axiosInstance from "../utils/axiosInstance";

import {
  FaTimes,
  FaEnvelope,
  FaPhone,
  FaAndroid,
  FaSignOutAlt,
  FaHome,
  FaUserCircle,
  FaBell,
  FaCogs,
  FaSearch,
} from "react-icons/fa";

import { Bot, Building2, Contact } from "lucide-react";
import { useCompany } from "../context/CompanyContext";

import logo from "../assets/logo.png";
import { NAV_SECTIONS } from "../config/navSections";
import SectionCard from "./navbar/SectionCard";
import GroupCard from "./navbar/GroupCard";
import ItemLink from "./navbar/ItemLink";

// ---------- MAIN NAVBAR COMPONENT ---------- //

const Navbar = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { company, companies, setCompany } = useCompany();

  const [profile, setProfile] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const menuPanelRef = useRef(null);

  const navigate = useNavigate();

  const manage =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "{}")
      : {};
  const isAdmin = manage?.role?.toLowerCase() === "admin";

  const getRole = useCallback(() => profile?.role || user?.role || "user", [profile, user]);

  // Dates
  moment.loadPersian({ dialect: "persian-modern" });
  const todayJalali = moment().format("jYYYY/jMM/jDD");
  const todayGregorian = moment().format("YYYY/MM/DD");

  // Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axiosInstance.get("/accounts/profile/");
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    if (isAuthenticated) fetchProfile();
  }, [isAuthenticated]);

  // PWA install event
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log("User choice:", outcome);
      setDeferredPrompt(null);
    }
  };

  const toggleMenu = () => setShowMenu((prev) => !prev);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setOpenSections({});
    menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (menuPanelRef.current) menuPanelRef.current.inert = !showMenu;
    if (!showMenu) return undefined;
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
        return;
      }
      if (event.key !== "Tab" || !menuPanelRef.current) return;
      const focusable = Array.from(menuPanelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !menuPanelRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !menuPanelRef.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showMenu, closeMenu]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  // Role-based version of NAV_SECTIONS (for normal nested menu)
  const filteredSections = useMemo(() => {
    const role = getRole()?.toLowerCase();
    return NAV_SECTIONS.map((section) => ({
      ...section,
      groups: section.groups.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!item.roles) return true;
          return item.roles.includes(role);
        }),
      })),
    }));
  }, [getRole]);

  // Flatten all leaf items for exact search
  const allMenuItems = useMemo(() => {
    const role = getRole()?.toLowerCase();
    const items = [];

    NAV_SECTIONS.forEach((section) => {
      section.groups.forEach((group) => {
        group.items.forEach((item) => {
          if (item.roles && !item.roles.includes(role)) return;
          items.push({
            ...item,
            sectionId: section.id,
            groupId: group.id,
            sectionLabel: section.label,
            groupLabel: group.label,
          });
        });
      });
    });

    // You can add account/admin items to search too if you like:
    // Account
    items.push({
      to: "/user/profile",
      label: "پروفایل من",
      icon: FaUserCircle,
      sectionId: "account",
      groupId: "account-main",
      sectionLabel: "حساب کاربری",
      groupLabel: "مدیریت حساب",
    });
    items.push({
      to: "/user/settings",
      label: "تنظیمات",
      icon: FaCogs,
      sectionId: "account",
      groupId: "account-main",
      sectionLabel: "حساب کاربری",
      groupLabel: "مدیریت حساب",
    });
    items.push({
      to: "/Messages",
      label: "پیام‌ها",
      icon: FaEnvelope,
      sectionId: "account",
      groupId: "account-main",
      sectionLabel: "حساب کاربری",
      groupLabel: "مدیریت حساب",
    });
    // Admin (only if admin)
    if (role === "admin") {
      items.push({
        to: "/AuditLogs",
        label: "لاگ کاربران",
        icon: FaCogs,
        sectionId: "admin",
        groupId: "admin-main",
        sectionLabel: "دسترسی ادمین",
        groupLabel: "مدیریت سیستم",
      });
      items.push({
        to: "/adminpanel",
        label: "پنل ادمین",
        icon: FaCogs,
        sectionId: "admin",
        groupId: "admin-main",
        sectionLabel: "دسترسی ادمین",
        groupLabel: "مدیریت سیستم",
      });
    }

    return items;
  }, [getRole]);

  // Search results = exact submenu items
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allMenuItems.filter((item) =>
      (item.label || "").toLowerCase().includes(q),
    );
  }, [allMenuItems, searchQuery]);

  const hasSearch = searchQuery.trim().length > 0;

  const displayUsername =
    profile?.username ||
    (isAuthenticated && user ? user.username : "کاربر سیستم");

  // ---------- RENDER ---------- //

  return (
    <div className="font-vazir" dir="ltr">
      {/* Top fixed bar */}
      <div className="fixed top-0 left-0 w-full z-40">
        <div className="bg-white/30 backdrop-blur  shadow-sm px-3 md:px-6 py-2 md:py-3 flex items-center justify-between flex-row-reverse md:flex-row">
          {/* Logo + title */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-start gap-2 md:gap-3 px-2 py-1.5 md:px-3 md:py-2 
                       bg-white border border-slate-200 rounded-2xl 
                       hover:border-indigo-400 hover:bg-indigo-50/80 
                       transition-colors duration-150 text-xs md:text-sm"
          >
            <div className="flex flex-col items-end">
              <span className="text-[0.75rem] md:text-sm font-semibold text-slate-900 truncate">
                نرم افزار تجارت کیان
              </span>
              <span className="hidden md:inline text-[0.65rem] text-slate-500">
                Ozhan Tejarat Kiyan – Dashboard
              </span>
            </div>
            <img
              src={logo}
              alt="Logo"
              className="w-7 h-7 md:w-9 md:h-9 object-contain rounded-xl shadow-sm"
            />
          </button>

          {/* Right side: date + PWA + burger */}
          <div className="flex items-center gap-2 md:gap-4">
            <label className="flex items-center gap-2 border border-slate-200 bg-white px-2 py-1.5 rounded-lg">
              <Building2 size={16} className="text-slate-500 shrink-0" />
              <select
                aria-label="انتخاب شرکت فعال"
                value={company?.id || ""}
                onChange={(event) => {
                  const selected = companies.find(
                    (item) => String(item.id) === event.target.value,
                  );
                  if (selected) setCompany(selected, true);
                }}
                className="max-w-32 md:max-w-52 bg-transparent text-xs md:text-sm text-slate-800 focus:outline-none"
              >
                {companies.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.full_name}
                  </option>
                ))}
              </select>
            </label>

            <div className="hidden sm:flex flex-col items-end text-[0.7rem] md:text-xs text-slate-500">
              <span>تاریخ شمسی: {todayJalali}</span>
              <span>تاریخ میلادی: {todayGregorian}</span>
            </div>

            {deferredPrompt && (
              <button
                onClick={handleInstallClick}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                           border border-indigo-200 bg-indigo-50/80 text-[0.7rem] text-indigo-700
                           hover:bg-indigo-100 transition-colors"
              >
                <FaAndroid className="text-xs" />
                <span>نصب نسخه PWA</span>
              </button>
            )}

            <button
              ref={menuButtonRef}
              type="button"
              aria-label={showMenu ? "بستن منوی اصلی" : "باز کردن منوی اصلی"}
              aria-expanded={showMenu}
              aria-controls="app-navigation-panel"
              className="p-2 rounded-xl hover:bg-slate-100/90 border border-slate-200/70 
                         text-slate-700 transition-colors"
              onClick={toggleMenu}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 6H20M4 12H16M4 18H13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Slide-over */}
      <div
        className={`fixed inset-0 z-40 flex justify-end transition-all duration-200 ${
          showMenu ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity duration-200 ${
            showMenu ? "opacity-100" : "opacity-0"
          }`}
          onClick={closeMenu}
        />

        {/* Panel */}
        <div
          id="app-navigation-panel"
          ref={menuPanelRef}
          role="dialog"
          aria-modal={showMenu}
          aria-label="منوی اصلی"
          aria-hidden={!showMenu}
          className={`relative h-full w-full sm:w-80 md:w-96 
                      bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950
                      shadow-2xl border-l border-slate-800
                      flex flex-col gap-4 px-4 py-4 sm:px-6 sm:py-6 
                      transition-transform duration-200 ease-out transform
                      ${showMenu ? "translate-x-0" : "translate-x-full"}
                      overflow-y-auto scrollbar-hidden`}
        >
          <div className="flex flex-col h-full text-slate-50">
            {/* Header / close */}
            <div className="flex items-center justify-between mb-2">
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="بستن منوی اصلی"
                className="text-slate-400 hover:text-slate-100 transition-colors text-xl"
                onClick={closeMenu}
              >
                <FaTimes />
              </button>

              <div className="flex flex-col items-end text-[0.7rem] text-slate-400 sm:hidden">
                <span>{todayJalali}</span>
                <span className="mt-0.5">{todayGregorian}</span>
              </div>
            </div>

            {/* User header */}
            <div className="flex flex-row-reverse items-center justify-between gap-3 rounded-2xl border border-slate-700/80 bg-slate-900/70 px-3 py-3 mb-2">
              <div className="flex flex-row-reverse items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-white text-sm font-bold shadow">
                  {(displayUsername || "ک").slice(0, 1)}
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-semibold text-slate-50">
                    {displayUsername}
                  </span>
                  <span className="text-[0.7rem] text-slate-400">
                    نقش: {getRole() === "admin" ? "مدیر سیستم" : "کاربر عادی"}
                  </span>
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] bg-slate-800 text-slate-300 border border-slate-600">
                اوژن تجارت کیان
              </span>
            </div>

            {/* Search box */}
            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  dir="rtl"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="جستجو در منو..."
                  className="w-full rounded-2xl bg-slate-900/70 border border-slate-700/80
                             px-3 py-2 pr-9 text-xs sm:text-sm text-slate-100
                             placeholder:text-slate-500 focus:outline-none
                             focus:ring-1 focus:ring-sky-500 focus:border-sky-500
                             text-right"
                />
                <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100 text-xs"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              {hasSearch && (
                <p className="mt-1 text-[0.7rem] text-slate-400 text-right">
                  نتایج برای:{" "}
                  <span className="text-sky-400">{searchQuery}</span>
                </p>
              )}
            </div>

            {/* Dashboard pill */}
            <div className="mb-3">
              <Link
                to="/"
                onClick={closeMenu}
                className="flex flex-row-reverse items-center justify-between gap-3 px-3 py-3 
                           rounded-2xl border border-indigo-500/60 bg-gradient-to-br 
                           from-indigo-500/20 to-sky-500/30 
                           text-indigo-50 shadow-[0_6px_18px_rgba(37,99,235,0.45)]"
              >
                <div className="flex flex-row-reverse items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-2xl bg-indigo-600">
                    <FaHome className="text-sm" />
                  </span>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-semibold">داشبورد</span>
                    <span className="text-[0.7rem] text-indigo-100/80">
                      خلاصه وضعیت و شاخص‌ها
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* MAIN SECTIONS */}
            <ul className="flex flex-col gap-2 text-right">
              {/* Search results as exact submenu items */}
              {hasSearch && (
                <li className="mb-2">
                  {searchResults.length === 0 ? (
                    <p className="text-[0.8rem] text-slate-400 text-center py-3">
                      هیچ آیتمی مطابق جستجو پیدا نشد.
                    </p>
                  ) : (
                    <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-2">
                      <p className="text-[0.75rem] text-slate-300 mb-2 text-right">
                        نتایج جستجو ({searchResults.length} مورد):
                      </p>
                      <ul className="space-y-1">
                        {searchResults.map((item) => (
                          <li key={`${item.sectionId}-${item.to}`}>
                            <button
                              type="button"
                              onClick={() => {
                                navigate(item.to);
                                closeMenu();
                              }}
                              className="w-full flex flex-col gap-0.5 px-3 py-2 rounded-xl
                                         bg-slate-900/80 hover:bg-slate-800/80
                                         text-xs sm:text-[0.8rem] text-slate-50 text-right transition-colors"
                            >
                              <div className="flex flex-row-reverse items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-slate-800/90">
                                  {item.icon && (
                                    <item.icon className="text-[0.7rem]" />
                                  )}
                                </span>
                                <span className="truncate font-medium">
                                  {item.label}
                                </span>
                              </div>
                              <span className="text-[0.65rem] text-slate-400">
                                {item.sectionLabel} • {item.groupLabel}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-3 border-t border-slate-800/80" />
                </li>
              )}

              {/* Full nested menu (shown always; search is just an overlay on top) */}
              {filteredSections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  open={!!openSections[section.id]}
                  onToggle={() => toggleSection(section.id)}
                >
                  <div className="pr-2 border-r border-dashed border-slate-700/80 space-y-1">
                    {section.groups.map((group) => {
                      const hasVisibleItems =
                        group.items && group.items.length > 0;
                      if (!hasVisibleItems) return null;
                      if (group.direct) {
                        const item = group.items[0];
                        return (
                          <ul key={group.id} className="mb-2">
                            <ItemLink item={item} closeMenu={closeMenu} />
                          </ul>
                        );
                      }
                      const groupOpen =
                        openSections[`${section.id}.${group.id}`];
                      return (
                        <GroupCard
                          key={group.id}
                          group={group}
                          open={!!groupOpen}
                          onToggle={() =>
                            toggleSection(`${section.id}.${group.id}`)
                          }
                        >
                          <ul className="py-1 pr-2 space-y-1">
                            {group.items.map((item) => (
                              <ItemLink
                                key={item.to}
                                item={item}
                                closeMenu={closeMenu}
                              />
                            ))}
                          </ul>
                        </GroupCard>
                      );
                    })}
                  </div>
                </SectionCard>
              ))}

              {/* ACCOUNT */}
              <li className="pb-1">
                <SectionCard
                  section={{
                    id: "account",
                    label: "حساب کاربری",
                    description: "پروفایل، اعلان‌ها و تنظیمات",
                    color: "slate",
                    icon: Contact,
                  }}
                  open={!!openSections["account"]}
                  onToggle={() => toggleSection("account")}
                >
                  <div className="pr-2 border-r border-dashed border-slate-700/80 space-y-1">
                    <ul className="py-1 pr-2 space-y-1">
                      <ItemLink
                        item={{
                          to: "/user/profile",
                          label: "پروفایل من",
                          icon: FaUserCircle,
                        }}
                        closeMenu={closeMenu}
                      />
                      <ItemLink
                        item={{
                          to: "/user/settings",
                          label: "تنظیمات",
                          icon: FaCogs,
                        }}
                        closeMenu={closeMenu}
                      />

                      {/* Notifications - collapsible inline */}
                      <li>
                        <button
                          onClick={() => toggleSection("account.notifications")}
                          className="flex flex-row-reverse items-center gap-2 px-3 py-2 text-xs sm:text-[0.8rem]
                            text-slate-100 hover:text-slate-50
                            hover:bg-slate-800/80 rounded-xl w-full transition-colors"
                        >
                          <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-slate-900/70">
                            <FaBell className="text-[0.7rem]" />
                          </span>
                          <span>اعلان‌ها</span>
                          <svg
                            className={`mr-auto text-[0.65rem] text-slate-300 transition-transform ${
                              openSections["account.notifications"]
                                ? "rotate-180"
                                : ""
                            }`}
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                          >
                            <path
                              d="M2 3L5 6L8 3"
                              stroke="currentColor"
                              strokeWidth="1"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div
                          className={`mt-1 pr-2 transition-all duration-200 origin-top ${
                            openSections["account.notifications"]
                              ? "max-h-[400px] opacity-100"
                              : "max-h-0 opacity-0 overflow-hidden"
                          }`}
                        >
                          <div className="rounded-2xl border border-slate-700/80 bg-slate-900/80 px-2 py-2">
                            <NotificationsDropdown />
                          </div>
                        </div>
                      </li>

                      <ItemLink
                        item={{
                          to: "/Messages",
                          label: "پیام‌ها",
                          icon: FaEnvelope,
                        }}
                        closeMenu={closeMenu}
                      />
                      {/* Logout */}
                      <li>
                        <button
                          onClick={handleLogout}
                          className="flex flex-row-reverse items-center gap-2 px-3 py-2 text-xs sm:text-[0.8rem]
                            text-rose-400 hover:text-rose-300
                            hover:bg-rose-900/30 rounded-xl w-full transition-colors"
                        >
                          <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-rose-900/60">
                            <FaSignOutAlt className="text-[0.7rem]" />
                          </span>
                          <span>خروج از سیستم</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                </SectionCard>
              </li>

              {/* ADMIN */}
              {isAdmin && (
                <li className="pb-1">
                  <SectionCard
                    section={{
                      id: "admin",
                      label: "دسترسی ادمین",
                      description: "مدیریت کاربران و تنظیمات سیستمی",
                      color: "rose",
                      icon: Bot,
                    }}
                    open={!!openSections["admin"]}
                    onToggle={() => toggleSection("admin")}
                  >
                    <div className="pr-2 border-r border-dashed border-slate-700/80 space-y-1">
                      <ul className="py-1 pr-2 space-y-1">
                        <ItemLink
                          item={{
                            to: "/AuditLogs",
                            label: "لاگ کاربران",
                            icon: FaCogs,
                          }}
                          closeMenu={closeMenu}
                        />
                        <ItemLink
                          item={{
                            to: "/adminpanel",
                            label: "پنل ادمین",
                            icon: FaCogs,
                          }}
                          closeMenu={closeMenu}
                        />
                      </ul>
                    </div>
                  </SectionCard>
                </li>
              )}
            </ul>

            {/* Support card */}
            <div className="mt-auto pt-4">
              <button
                className="w-full flex flex-row-reverse items-center justify-between gap-2 
                           px-4 py-3 rounded-2xl 
                           bg-gradient-to-r from-emerald-500 to-sky-500 
                           text-white text-xs sm:text-sm
                           shadow-[0_10px_28px_rgba(16,185,129,0.55)]
                           hover:shadow-[0_14px_36px_rgba(56,189,248,0.75)]
                           transition-all"
                onClick={() => (window.location.href = `tel:${"02191006662"}`)}
              >
                <div className="flex flex-row-reverse items-center gap-2">
                  <span className="w-9 h-9 rounded-2xl bg-white/15 flex items-center justify-center">
                    <FaPhone className="text-base" />
                  </span>
                  <div className="flex flex-col items-end">
                    <span className="font-semibold text-[0.85rem]">
                      تماس با پشتیبانی
                    </span>
                    <span className="text-[0.8rem] text-emerald-50/90">
                      021-91006662
                    </span>
                  </div>
                </div>
                <span className="text-[0.7rem] text-emerald-50/80">
                  همه روزه ۹ تا ۱۷
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollbar hidden helper */}
      <style jsx>{`
        .scrollbar-hidden::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hidden {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default Navbar;
